import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

interface User {
  name: string;
  email: string;
  avatar?: string | null;
}

interface AvatarGroupProps {
  users: User[];
  maxDisplay?: number;
  size?: "sm" | "md" | "lg";
}

export default function AvatarGroup({
  users,
  maxDisplay = 2,
  size = "md",
}: AvatarGroupProps) {
  if (!users || users.length === 0) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  const displayUsers = users.slice(0, maxDisplay);
  const remainingCount = users.length - maxDisplay;
  const remainingUsers = users.slice(maxDisplay);

  const sizeClasses = {
    sm: "w-6 h-6 text-[9px]",
    md: "w-8 h-8 text-[10px]",
    lg: "w-10 h-10 text-xs",
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <TooltipProvider>
      <div className="flex items-center">
        {displayUsers.map((user, index) => (
          <Tooltip key={user.email || index}>
            <TooltipTrigger asChild>
              <div
                className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-600 border-2 border-white bg-muted text-foreground ${
                  index > 0 ? "-ml-2" : ""
                } hover:z-10 cursor-pointer transition-transform hover:scale-110`}
                style={{ zIndex: displayUsers.length - index }}
              >
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <span>{getInitials(user.name)}</span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {user.name}
                {user.email && user.email !== user.name && (
                  <span className="text-muted-foreground"> ({user.email})</span>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`${sizeClasses[size]} rounded-full flex items-center justify-center bg-muted text-muted-foreground font-600 border-2 border-white -ml-2 cursor-pointer hover:z-10 transition-transform hover:scale-110`}
                style={{ zIndex: 0 }}
              >
                <span>+{remainingCount}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{remainingCount} more</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

